/**
 * Activities Page — task list grouped by overdue/today/upcoming/completed.
 */

import { useEffect, useState, useCallback } from 'react'
import { Plus, CheckSquare, Phone, Pencil } from 'lucide-react'
import { useCrmStore } from '@/stores/crmStore'
import { useCRM } from '@/hooks/useCRM'
import { ActivityItem } from '@/components/crm/ActivityItem'
import { QuickActivityCreate } from '@/components/crm/QuickActivityCreate'
import {
  Button,
  Spinner,
  EmptyState,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui'
import type { Activity, ActivityType } from '@/types/crm'

export function ActivitiesPage() {
  const { fetchActivities, completeActivity, fetchOverdueCount } = useCRM()
  const activities = useCrmStore((s) => s.activities)
  const isLoading = useCrmStore((s) => s.isLoadingActivities)

  const [typeFilter, setTypeFilter] = useState<string>('')
  const [completedFilter, setCompletedFilter] = useState<boolean | undefined>(false)
  const [showCreate, setShowCreate] = useState(false)
  const [createType, setCreateType] = useState<ActivityType>('task')

  const loadActivities = useCallback(() => {
    fetchActivities({
      activityType: typeFilter || undefined,
      isCompleted: completedFilter,
    })
  }, [fetchActivities, typeFilter, completedFilter])

  useEffect(() => {
    loadActivities()
    fetchOverdueCount()
  }, [loadActivities, fetchOverdueCount])

  const handleComplete = useCallback(async (activityId: string) => {
    const result = await completeActivity(activityId)
    if (result) {
      loadActivities()
      fetchOverdueCount()
    }
  }, [completeActivity, loadActivities, fetchOverdueCount])

  const handleCreate = (type: ActivityType) => {
    setCreateType(type)
    setShowCreate(true)
  }

  // Group activities
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const todayEnd = new Date(todayStart.getTime() + 86400000)
  const weekEnd = new Date(todayStart.getTime() + 7 * 86400000)

  const overdue: Activity[] = []
  const today: Activity[] = []
  const upcoming: Activity[] = []
  const noDue: Activity[] = []
  const completed: Activity[] = []

  for (const a of activities) {
    if (a.isCompleted) {
      completed.push(a)
    } else if (a.dueDate) {
      const due = new Date(a.dueDate)
      if (due < todayStart) overdue.push(a)
      else if (due < todayEnd) today.push(a)
      else if (due < weekEnd) upcoming.push(a)
      else upcoming.push(a)
    } else {
      noDue.push(a)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border">
        <div className="py-3 flex items-center gap-3">
          <Select
            value={typeFilter || 'all'}
            onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger size="sm" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="task">Tasks</SelectItem>
              <SelectItem value="note">Notes</SelectItem>
              <SelectItem value="call">Calls</SelectItem>
              <SelectItem value="meeting">Meetings</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={completedFilter === undefined ? 'all' : completedFilter ? 'completed' : 'open'}
            onValueChange={(v) => setCompletedFilter(v === 'all' ? undefined : v === 'completed')}
          >
            <SelectTrigger size="sm" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex-1" />

          <div className="flex gap-2">
            <Button leftIcon={<Plus className="w-3.5 h-3.5" />} onClick={() => handleCreate('task')}>Task</Button>
            <Button variant="secondary" leftIcon={<Pencil className="w-3.5 h-3.5" />} onClick={() => handleCreate('note')}>Note</Button>
            <Button variant="secondary" leftIcon={<Phone className="w-3.5 h-3.5" />} onClick={() => handleCreate('call')}>Call</Button>
          </div>
        </div>
      </div>

      {/* Activity list */}
      <div className="flex-1 overflow-auto py-4">
        {isLoading && activities.length === 0 ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : activities.length === 0 ? (
          <EmptyState
            icon={CheckSquare}
            title="No activities yet"
            description="Create a task or log a note to get started"
          />
        ) : (
          <div className="space-y-6">
            {overdue.length > 0 && (
              <ActivityGroup label="Overdue" count={overdue.length} color="text-destructive" activities={overdue} onComplete={handleComplete} />
            )}
            {today.length > 0 && (
              <ActivityGroup label="Today" count={today.length} color="text-info" activities={today} onComplete={handleComplete} />
            )}
            {upcoming.length > 0 && (
              <ActivityGroup label="Upcoming" count={upcoming.length} color="text-muted-foreground" activities={upcoming} onComplete={handleComplete} />
            )}
            {noDue.length > 0 && (
              <ActivityGroup label="No Due Date" count={noDue.length} color="text-muted-foreground" activities={noDue} onComplete={handleComplete} />
            )}
            {completed.length > 0 && (
              <ActivityGroup label="Completed" count={completed.length} color="text-success" activities={completed} onComplete={handleComplete} />
            )}
          </div>
        )}
      </div>

      {showCreate && (
        <QuickActivityCreate
          activityType={createType}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadActivities(); fetchOverdueCount() }}
        />
      )}
    </div>
  )
}

function ActivityGroup({ label, count, color, activities, onComplete }: {
  label: string; count: number; color: string; activities: Activity[]; onComplete: (id: string) => void
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-xs font-semibold uppercase tracking-wider ${color}`}>{label}</span>
        <span className="text-xs text-muted-foreground">({count})</span>
      </div>
      <div className="space-y-1">
        {activities.map((a) => (
          <ActivityItem key={a.id} activity={a} onComplete={onComplete} />
        ))}
      </div>
    </div>
  )
}
